import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  TrendingDown,
  Receipt,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { PageLoader } from '@/components/ui/Spinner';
import { CategoryPieChart, getCategoryColor } from '@/components/charts/CategoryPieChart';
import { TrendLineChart } from '@/components/charts/TrendLineChart';
import {
  useDashboardSummary,
  useDashboardTrends,
  useRecentTransactions,
} from '@/hooks/useDashboard';
import { useTranslation } from '@/i18n';
import type { Locale } from '@/i18n';
import type { Transaction } from '@/types/transaction';

type Period = 'week' | 'month' | 'year';

const currencyMap: Record<Locale, string> = {
  'pt-BR': 'BRL',
  'en-GB': 'GBP',
  'es-ES': 'EUR',
  'fr-FR': 'EUR',
  'it-IT': 'EUR',
  'ar': 'SAR',
  'zh-CN': 'CNY',
};

export const DashboardPage: React.FC = () => {
  const { t, locale } = useTranslation();
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>('month');

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyMap[locale],
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(locale, {
      day: '2-digit',
      month: 'short',
    });
  };

  // Helper to safely get category key from string or object
  const getCategoryKey = (category: unknown): string => {
    if (typeof category === 'string') return category;
    if (category && typeof category === 'object') {
      const cat = category as { name?: string; nameEn?: string; id?: string };
      return cat.nameEn || cat.name || cat.id || 'other';
    }
    return 'other';
  };

  // Helper to get translated category label
  const getCategoryLabel = (category: unknown): string => {
    const key = getCategoryKey(category);
    return t.app.categoryLabels[key as keyof typeof t.app.categoryLabels] || key;
  };

  const { data: summary, isLoading: loadingSummary } = useDashboardSummary(period);
  const { data: trends, isLoading: loadingTrends } = useDashboardTrends(period);
  const { data: recentTransactions, isLoading: loadingRecent } =
    useRecentTransactions(5);

  const isLoading = loadingSummary || loadingTrends || loadingRecent;

  // Default empty values when no data (merge with defaults for each field)
  const dashboardSummary = {
    totalIncome: summary?.totalIncome ?? 0,
    totalExpense: summary?.totalExpense ?? 0,
    balance: summary?.balance ?? 0,
    transactionCount: summary?.transactionCount ?? 0,
    averageExpense: summary?.averageExpense ?? 0,
    topCategories: summary?.topCategories ?? [],
  };

  const dashboardTrends = Array.isArray(trends) ? trends : [];
  const transactions: Transaction[] = recentTransactions || [];

  if (isLoading) {
    return <PageLoader message={t.app.dashboard.loading} />;
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t.app.dashboard.title}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {t.app.dashboard.subtitle}
          </p>
        </div>

        {/* Period selector */}
        <div className="flex items-center gap-2 bg-gray-100 dark:bg-secondary-800 rounded-lg p-1">
          {(['week', 'month', 'year'] as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                period === p
                  ? 'bg-white dark:bg-secondary-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              {t.app.dashboard.period[p]}
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Balance */}
        <Card
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/app/transactions')}
        >
          <CardContent>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t.app.dashboard.cards.balance}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {formatCurrency(dashboardSummary.balance)}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-primary-100 dark:bg-primary-900/30">
                <DollarSign className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Income */}
        <Card
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/app/transactions?type=income')}
        >
          <CardContent>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t.app.dashboard.cards.income}
                </p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                  {formatCurrency(dashboardSummary.totalIncome)}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-green-100 dark:bg-green-900/30">
                <TrendingUp className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Expenses */}
        <Card
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/app/transactions?type=expense')}
        >
          <CardContent>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t.app.dashboard.cards.expenses}
                </p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1">
                  {formatCurrency(dashboardSummary.totalExpense)}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-red-100 dark:bg-red-900/30">
                <TrendingDown className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Transactions count */}
        <Card
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/app/transactions')}
        >
          <CardContent>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t.app.dashboard.cards.transactions}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {dashboardSummary.transactionCount}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-gray-100 dark:bg-secondary-700">
                <Receipt className="w-6 h-6 text-gray-600 dark:text-gray-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Trend chart */}
        <Card>
          <CardHeader>
            <CardTitle>{t.app.dashboard.charts.trend}</CardTitle>
          </CardHeader>
          <CardContent>
            <TrendLineChart data={dashboardTrends} />
          </CardContent>
        </Card>

        {/* Category pie chart */}
        <Card>
          <CardHeader>
            <CardTitle>{t.app.dashboard.charts.byCategory}</CardTitle>
          </CardHeader>
          <CardContent>
            <CategoryPieChart data={dashboardSummary.topCategories} />
          </CardContent>
        </Card>
      </div>

      {/* Recent transactions and top categories */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent transactions */}
        <Card className="lg:col-span-2">
          <CardHeader
            action={
              <a
                href="/app/transactions"
                className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                {t.app.dashboard.viewAll}
              </a>
            }
          >
            <CardTitle>{t.app.dashboard.recent}</CardTitle>
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <div className="text-center py-8">
                <Receipt className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                <p className="text-gray-500 dark:text-gray-400">
                  {t.app.common.noResults}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {transactions.map((transaction) => (
                  <div
                    key={transaction.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-secondary-700/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center relative">
                        <div
                          className="absolute inset-0 rounded-full opacity-20"
                          style={{ backgroundColor: getCategoryColor(getCategoryKey(transaction.category)) }}
                        />
                        {transaction.type === 'income' ? (
                          <ArrowUpRight
                            className="w-5 h-5 relative z-10"
                            style={{ color: getCategoryColor(getCategoryKey(transaction.category)) }}
                          />
                        ) : (
                          <ArrowDownRight
                            className="w-5 h-5 relative z-10"
                            style={{ color: getCategoryColor(getCategoryKey(transaction.category)) }}
                          />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {transaction.description}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {getCategoryLabel(transaction.category)} -{' '}
                          {formatDate(transaction.date)}
                        </p>
                      </div>
                    </div>
                    <p
                      className={`font-semibold ${
                        transaction.type === 'income'
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}
                    >
                      {transaction.type === 'income' ? '+' : '-'}
                      {formatCurrency(transaction.amount)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top categories */}
        <Card>
          <CardHeader
            action={
              <button
                onClick={() => navigate('/app/categories')}
                className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                {t.app.dashboard.viewAll}
              </button>
            }
          >
            <CardTitle>{t.app.dashboard.topCategories}</CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardSummary.topCategories.length === 0 ? (
              <div className="text-center py-8">
                <DollarSign className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                <p className="text-gray-500 dark:text-gray-400">
                  {t.app.common.noResults}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {dashboardSummary.topCategories.slice(0, 5).map((category, index) => (
                  <div key={getCategoryKey(category.category) + index}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {getCategoryLabel(category.category)}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {category.percentage.toFixed(1)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-secondary-700 rounded-full h-2">
                      <div
                        className="h-2 rounded-full transition-all duration-500"
                        style={{
                          width: `${category.percentage}%`,
                          backgroundColor: getCategoryColor(getCategoryKey(category.category)),
                        }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {formatCurrency(category.amount)} ({category.count} {t.app.dashboard.items})
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
